import { pickLocale, type Group } from '../schema';
import { isCharacteristicVisible, useIsCharacteristicVisible, useIsGroupVisible, useRuleResults } from '../rules/RuleResultsContext';
import { rendererFor } from '../api/renderers';
import { useTranslation } from '../i18n';
import { CharacteristicView, useCharacteristicRenderer } from './characteristics';

interface GroupBlockProps {
    group: Group;
    locale: string | undefined;
    /**
     * Whether to render the Group's own heading.
     *
     * False wherever the tab strip is on screen: it already names every Group,
     * so a heading right underneath repeats the word the customer just clicked
     * — "Dein Bedarf" in the tab and "Dein Bedarf" again as the first line.
     * The heading stays for the layouts without a strip (a single Group),
     * where it is the only thing naming the section.
     */
    showTitle?: boolean;
}

/**
 * Renders a Group when it has at least one visible Characteristic. The
 * Group itself collapses (returns null) when every Characteristic is
 * hidden — by admin visibility, by an active rule, or because a selection-
 * type Characteristic lost all its Values to blacklist rules. Mirrors
 * legacy storefront `hideEmptyGroups` (configurator.js:594-635).
 */
export function GroupBlock({ group, locale, showTitle = true }: GroupBlockProps) {
    const { t } = useTranslation();
    const groupVisible = useIsGroupVisible(group);
    if (!groupVisible) return null;

    const title = pickLocale(group.title, locale) ?? t('fallback.group_title', { id: group.id });

    return (
        <section className="cfg-group" data-configento-group-id={group.id} data-configento-group-key={group.altTitle}>
            {showTitle && <h2 className="cfg-group__title">{title}</h2>}
            <div className="cfg-group__chars">
                {group.characteristics.map((char) => (
                    <CharacteristicSlot key={char.id} char={char} locale={locale} siblings={group.characteristics} />
                ))}
            </div>
        </section>
    );
}

/**
 * Slot that drops the Characteristic when it shouldn't be visible —
 * `visibility=hidden|backend`, rule-blacklisted, or (for selection types)
 * every Value got blacklisted. The hook re-subscribes to the rule-results
 * context per-Characteristic; React skips the re-render when the
 * predicate doesn't flip.
 *
 * Exported so the `no_groups` layout in GroupNavigator can reuse the same
 * visibility gate when rendering all characteristics flat (no per-group
 * section or title).
 */
export function CharacteristicSlot({
    char,
    locale,
    siblings,
}: {
    char: Parameters<typeof CharacteristicView>[0]['characteristic'];
    locale: string | undefined;
    /** The Group's Characteristics — a combining custom renderer merges those with its class. */
    siblings?: readonly Parameters<typeof CharacteristicView>[0]['characteristic'][];
}) {
    const visible = useIsCharacteristicVisible(char);
    const custom = useCharacteristicRenderer(char);
    const rules = useRuleResults();
    if (!visible) return null;
    if (custom?.renderer.combine && siblings) {
        // One widget for every visible member, drawn where the first one sits.
        const members = siblings.filter((c) =>
            isCharacteristicVisible(c, rules) && rendererFor(c.cssClass)?.name === custom.name);
        if (members[0]?.id !== char.id) return null;
        return <CharacteristicView characteristic={char} locale={locale} members={members} />;
    }
    return <CharacteristicView characteristic={char} locale={locale} />;
}
